import { CloudUploadOutlined } from '@ant-design/icons'
import { Button, DatePicker, Form, Input, message, Select, Upload } from 'antd'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, { memo, useCallback, useState } from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import ContentBlockWrapper from '../../../components/ContentBlockWrapper'
import PageTitle from '../../../components/PageTitle'
import { DATE_FORMAT_LIST } from '../../../constants'
import { Container } from '../../../layouts/Container/Container'
import DashboardLayout from '../../../layouts/DashboardLayout'
import validator from '../../../validator'
import {
  DraggerUploadStyled,
  FormActionFooter,
  ViewDetailContent,
} from './BoardResolutionMinutesCreateStyled'

const ISSUE_LEVEL_OPTIONS = [
  { label: 'Nghị quyết', value: 'Nghị quyết' },
  { label: 'Quyết định', value: 'Quyết định' },
]

const BoardResolutionMinutesCreatePage = props => {
  const { Option } = Select
  const { Dragger } = Upload
  const {
    loadingAnimationStore,
    boardResolutionMinutesStore,
    history,
    fileStore,
  } = props

  const [form] = Form.useForm()
  const [fileList, setFileList] = useState([])

  const handleAddFileToUpload = () => false
  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
  }

  const handleChangeFile = useCallback(
    info => {
      form.setFieldsValue({
        file_id: info.fileList,
      })
      setFileList(info.fileList)
    },
    [form]
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSubmitForm = async values => {
    loadingAnimationStore.showSpinner(true)

    const submitData = { ...values }
    // Avoid UTC midnight shift (-1 day): send noon local → stable calendar date
    if (submitData.issue_date) {
      submitData.issue_date = moment(submitData.issue_date)
        .hour(12)
        .minute(0)
        .second(0)
        .toISOString()
    }

    const uploadPromises = submitData.file_id.map(file => {
      const formData = new FormData()
      formData.append('file', file.originFileObj)
      return fileStore.uploadFile(formData)
    })

    Promise.all(uploadPromises)
      .then(responses => {
        submitData.file_id = responses.map(response => response.data.file_id)
        handleCreateBoardHDQT(submitData)
      })
      .catch(error => {
        message.error(error.vi)
        loadingAnimationStore.showSpinner(false)
      })
  }

  const handleCreateBoardHDQT = useCallback(
    submitData => {
      boardResolutionMinutesStore
        .createBoardResolutionMinutes(submitData)
        .then(() => {
          boardResolutionMinutesStore.resetFilter()
          history.push({
            pathname: '/board-resolution-minutes-hdqt',
          })
          loadingAnimationStore.showSpinner(false)
          message.success(`Tạo quyết định mới thành công`)
        })
        .catch(error => {
          loadingAnimationStore.showSpinner(false)
          message.error(error.vi)
        })
    },
    [boardResolutionMinutesStore, history, loadingAnimationStore]
  )

  const handleCancel = () => {
    form.resetFields()
    boardResolutionMinutesStore.resetFilter()
    history.push(`/board-resolution-minutes-hdqt`)
  }

  const statusOptions = [
    { label: 'Đã bãi bỏ', value: 'ABOLISHED' },
    { label: 'Hết hiệu lực', value: 'EXPIRED' },
    { label: 'Còn hiệu lực', value: 'ACTIVE' },
  ]

  const renderFormContent = () => (
    <ContentBlockWrapper>
      <Container maxWidth={820}>
        <Form
          scrollToFirstError={true}
          {...layout}
          form={form}
          name={'add-board-form'}
          onFinish={handleSubmitForm}>
          <Form.Item
            name={'document_name'}
            label={'Tên văn bản'}
            rules={[
              { required: true, message: 'Vui lòng nhập tên văn bản' },
              { validator: validator.validateInputString },
            ]}>
            <Input placeholder={'Nhập tên văn bản'} />
          </Form.Item>

          <Form.Item
            name={'document_number'}
            label={'Số quyết định ban hành'}
            rules={[
              {
                required: true,
                message: 'Vui lòng nhập số quyết định ban hành',
              },
              { validator: validator.validateInputString },
            ]}>
            <Input placeholder={'Nhập số quyết định ban hành'} />
          </Form.Item>

          <Form.Item
            name={'issue_date'}
            label={'Ngày ban hành'}
            rules={[
              {
                required: true,
                message: 'Vui lòng chọn ngày ban hành',
              },
            ]}>
            <DatePicker style={{ width: '100%' }} format={DATE_FORMAT_LIST} />
          </Form.Item>

          <Form.Item
            name={'issue_level'}
            label={'Phân loại'}
            rules={[{ required: true, message: 'Vui lòng chọn phân loại' }]}>
            <Select
              allowClear
              placeholder={'Chọn phân loại'}
              optionFilterProp={'label'}>
              {ISSUE_LEVEL_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value} label={opt.label}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* <Form.Item name={'contruction_board'} label={'Ban chủ trì xây dựng'}>
            <Input placeholder={'Nhập ban chủ trì xây dựng'} />
          </Form.Item> */}

          <Form.Item name={'status'} label={'Trạng thái'}>
            <Select
              showSearch
              allowClear
              placeholder="Trạng thái"
              filterOption={true}
              optionFilterProp="label">
              {statusOptions.map(opt => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name={'file_id'}
            label={'Tài liệu đính kèm'}
            rules={[{ required: true, message: 'Vui lòng thêm tài liệu' }]}>
            <Dragger
              multiple
              valuePropName={'fileList'}
              fileList={fileList}
              onChange={handleChangeFile}
              beforeUpload={handleAddFileToUpload}>
              <DraggerUploadStyled>
                <CloudUploadOutlined />
                <span>Kéo thả hoặc click vào để tải lên tài liệu</span>
              </DraggerUploadStyled>
            </Dragger>
          </Form.Item>
          <FormActionFooter>
            <Button onClick={handleCancel}>Huỷ bỏ</Button>
            <Button
              style={{ marginLeft: 10 }}
              type={'primary'}
              htmlType={'submit'}>
              Tạo quyết định
            </Button>
          </FormActionFooter>
        </Form>
      </Container>
    </ContentBlockWrapper>
  )

  return (
    <DashboardLayout>
      <Helmet>
        <title>Tạo mới quyết định nghị quyết</title>
      </Helmet>
      <PageTitle
        location={props.location}
        title={'Tạo quyết định nghị quyết HĐQT'}
        routerGoback={'/utility/vbpc'}
      />
      <ViewDetailContent>{renderFormContent()}</ViewDetailContent>
    </DashboardLayout>
  )
}

BoardResolutionMinutesCreatePage.propTypes = {}

export default memo(
  inject(
    'loadingAnimationStore',
    'commonStore',
    'documentStore',
    'fileStore',
    'boardResolutionMinutesStore'
  )(observer(BoardResolutionMinutesCreatePage))
)
